import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { EnvToken, SeoSegmentsToken } from '@app/core/env.tokens';
import { PriceGuideService } from '@services/http/priceGuide.service';
import { ActivatedRoute, RouterLink } from '@angular/router';
import {
  AIPriceGuidesByCatResponse,
  AIPriceGuideListItem,
} from '@models/guides/aiPriceGuide.model';
import { CommonModule } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { BreadcrumbListComponent } from '@components/shared/breadcrumb-list/breadcrumb-list.component';
import { catchError, of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SEOService } from '@services/logic/seo.service';
import { JsonLdPipe } from '@app/pipes/json-ld-pipe';
import { NgOptimizedImage } from '@angular/common';
import { TranslatePipe } from '@app/pipes/translate.pipe';
import { FormatCurrencyRangePipe } from '@app/pipes/format-currency-range.pipe';

@Component({
  selector: 'app-ai-priceguides-index',
  standalone: true,
  imports: [
    FormatCurrencyRangePipe,
    RouterLink,
    MatIcon,
    BreadcrumbListComponent,
    CommonModule,
    JsonLdPipe,
    NgOptimizedImage,
    TranslatePipe,
  ],
  providers: [TranslatePipe],
  templateUrl: './ai-priceguides-index.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './ai-priceguides-index.component.scss',
})
export class AiPriceguidesIndexComponent implements OnInit, OnDestroy {
  protected readonly env = inject(EnvToken);
  protected readonly seg = inject(SeoSegmentsToken);
  private readonly cdr = inject(ChangeDetectorRef);

  public aiPriceGuides: AIPriceGuideListItem[] = [];
  private destroy$ = new Subject<void>();
  mainCategory: string = '';
  subCategory: string = '';
  subSubCategory: string = '';
  hasHeadCategory: boolean = false;
  hasSubCategory: boolean = false;
  hasSubSubCategory: boolean = false;
  activeCategory: string = '';
  basePath: string = '';
  partsOfUrl: { name: string; url: string }[] = [];

  mainCategoryTitle: string = '';
  subCategoryTitle: string = '';
  subSubCategoryTitle: string = '';
  activeCategoryTitle: string = '';

  public breadCrumSchema: any;

  loadingPriceGuides: boolean = true;

  constructor(
    private priceGuideService: PriceGuideService,
    private activatedRoute: ActivatedRoute,
    private seoService: SEOService,
    private t: TranslatePipe,
  ) {}

  ngOnInit() {
    this.findCategoryForIndex();
    this.createBasePath();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  findCategoryForIndex() {
    const headCategory = this.activatedRoute.snapshot.params['headCategory'];
    const subCategory = this.activatedRoute.snapshot.params['subCategory'];
    const subSubCategory =
      this.activatedRoute.snapshot.params['subSubCategory'];
    if (headCategory) {
      this.mainCategory = headCategory;
      this.hasHeadCategory = true;
    }
    if (subCategory) {
      this.subCategory = subCategory;
      this.hasSubCategory = true;
    }
    if (subSubCategory) {
      this.subSubCategory = subSubCategory;
      this.hasSubSubCategory = true;
    }

    // Set activeCategory to the deepest available category
    this.activeCategory = subSubCategory || subCategory || headCategory;
    if (this.activeCategory) {
      this.fetchAIPriceGuidesByCat(this.activeCategory);
    }
  }

  createBasePath() {
    let basePath = this.mainCategory;
    if (this.hasSubCategory) {
      basePath += '/' + this.subCategory;
    }
    if (this.hasSubSubCategory) {
      basePath += '/' + this.subSubCategory;
    }
    this.basePath = basePath;
    this.setBreadCrumb();
  }

  fetchAIPriceGuidesByCat(category: string) {
    if (category) {
      this.priceGuideService
        .fetchAIPriceGuidesByCat(category)
        .pipe(
          takeUntil(this.destroy$),
          catchError((err) => {
            return of([]);
          }),
        )
        .subscribe((data: AIPriceGuidesByCatResponse | any[]) => {
          if (data && 'data' in data && data.data && data.data.length > 0) {
            this.aiPriceGuides = data.data;
          } else {
            this.aiPriceGuides = [];
          }
          this.loadingPriceGuides = false;
          this.cdr.markForCheck();
        });
    }
  }

  private setBreadCrumb() {
    this.mainCategoryTitle = this.transformCategoryToReadableText(
      this.mainCategory,
    );
    this.subCategoryTitle = this.transformCategoryToReadableText(
      this.subCategory,
    );
    this.subSubCategoryTitle = this.transformCategoryToReadableText(
      this.subSubCategory,
    );
    this.activeCategoryTitle = this.transformCategoryToReadableText(
      this.activeCategory,
    );

    this.partsOfUrl.push({
      name: this.mainCategoryTitle ?? this.mainCategory,
      url: `${this.env.domain}/${this.mainCategory}/${this.seg.suffix.hubIndex}`,
    });
    if (this.mainCategory && this.subCategory && !this.subSubCategory) {
      this.partsOfUrl.push({
        name: this.subCategoryTitle,
        url: `${this.env.domain}/${this.mainCategory}/${this.subCategory}/${this.seg.suffix.hubIndex}`,
      });
    } else if (this.subSubCategory && this.subCategory && this.mainCategory) {
      this.partsOfUrl.push({
        name: this.subSubCategoryTitle ?? this.subCategory,
        url: `${this.env.domain}/${this.mainCategory}/${this.subCategory}/${this.subSubCategory}/${this.seg.suffix.hubIndex}`,
      });
    }
    // This page's own URL. `basePath` is the full category path the route is
    // mounted under; the previous canonical used
    // `activatedRoute.snapshot.url`, which only holds the segments THIS child
    // route matched — it dropped `:headCategory` and the leading slash and so
    // resolved to a bogus host (`handyhand.dkvinduespudsning/...`). The
    // breadcrumb trail's last crumb is this page, so both must use it:
    // `activeCategory` is only the deepest single segment, which on a nested
    // route points at a different page than the one being rendered.
    const canonicalUrl = `${this.env.domain}/${this.basePath}/${this.seg.indexPage.aiPriceGuide}`;

    this.partsOfUrl.push({
      name: this.t.transform('PAGES.AI_PRICE_GUIDES_INDEX.BREADCRUMB_INDEX'),
      url: canonicalUrl,
    });
    this.injectBreadCrumbSchema();

    const imageGuide = this.aiPriceGuides.find((guide) => guide.heroImg !== '');
    // Set meta details
    this.seoService.setPageMeta({
      metaTitle: `${this.t.transform('PAGES.AI_PRICE_GUIDES_INDEX.META_TITLE_PREFIX')} ${this.activeCategoryTitle}`,
      metaDesc: `${this.t.transform('PAGES.AI_PRICE_GUIDES_INDEX.META_DESC_PREFIX')} ${this.activeCategoryTitle}${this.t.transform('PAGES.AI_PRICE_GUIDES_INDEX.META_DESC_SUFFIX')}`,
      canonicalUrl,
      img: imageGuide?.heroImg ?? '',
    });
  }

  private injectBreadCrumbSchema() {
    this.breadCrumSchema = {
      '@context': 'http://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [],
    };
    this.partsOfUrl.forEach((e, i) => {
      this.breadCrumSchema.itemListElement.push({
        '@type': 'ListItem',
        // Google requires 1-based breadcrumb positions
        position: i + 1,
        item: {
          '@id': e.url,
          name: e.name,
        },
      });
    });
  }

  transformCategoryToReadableText(category: string): string {
    let readableText = category;
    readableText = readableText.replace(/-/g, ' ');
    readableText = readableText.replace(/oe/g, 'ø');
    readableText = readableText.replace(/aa/g, 'å');
    readableText = readableText.replace(/ae/g, 'æ');
    return readableText;
  }

  // Helper method to generate the proper URL for AI price guides
  getPriceGuideUrl(priceGuide: AIPriceGuideListItem): string {
    const mainCat = this.mainCategory;

    return `/${mainCat}/${priceGuide.slug}/${this.seg.suffix.aiPriceGuide}`;
  }

  protected readonly environment = this.env;
}
